import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Waves,
  Code,
  Lock,
  Globe,
  CreditCard,
  Question,
  User,
  ArrowLeft,
  Copy,
  CheckCircle,
  CaretDown,
  Lightning,
  Sun,
  Moon
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Logo, LogoText } from '../components/Logo';

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group">
      <button onClick={handleCopy} className="absolute top-3 right-3 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'var(--bg-main)', color: 'var(--text-muted)' }} data-testid="copy-code">
        {copied ? <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </button>
      <pre className="border rounded-lg p-4 overflow-x-auto text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }}>
        <code className="font-mono text-xs leading-relaxed whitespace-pre" style={{ color: 'var(--text-secondary)' }}>{code}</code>
      </pre>
    </div>
  );
}

function EndpointCard({ method, path, description, authRequired, body, response }) {
  const [open, setOpen] = useState(false);
  const methodColors = {
    GET: 'bg-emerald-500/20 text-emerald-400',
    POST: 'bg-blue-500/20 text-blue-400',
    PUT: 'bg-amber-500/20 text-amber-400',
    DELETE: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="border rounded-lg overflow-hidden transition-colors" style={{ borderColor: 'var(--border-main)' }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-3 text-left transition-colors" style={{ color: 'var(--text-primary)' }}>
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${methodColors[method] || 'bg-neutral-500/20 text-neutral-400'}`}>{method}</span>
        <code className="font-mono text-sm flex-1">{path}</code>
        {authRequired && <Lock weight="fill" className="w-3.5 h-3.5 text-amber-400" title="Auth required" />}
        <CaretDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
      </button>
      {open && (
        <div className="p-4 border-t space-y-3" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{description}</p>
          {authRequired && (
            <div className="flex items-center gap-2 text-xs text-amber-400">
              <Lock weight="fill" className="w-3 h-3" /> Authentication required (access_token cookie)
            </div>
          )}
          {body && (
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Request Body:</p>
              <CodeBlock code={JSON.stringify(body, null, 2)} />
            </div>
          )}
          {response && (
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Response:</p>
              <CodeBlock code={JSON.stringify(response, null, 2)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, description, children }) {
  return (
    <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
      <div className="p-5 border-b" style={{ borderColor: 'var(--border-main)' }}>
        <h2 className="font-bold flex items-center gap-2"><Icon weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />{title}</h2>
        {description && <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{description}</p>}
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </div>
  );
}

export default function ApiDocs() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const baseUrl = 'https://index24hrs.com/api';

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="apidocs-logo">
            <Logo size="md" />
            <LogoText size="md" />
          </Link>
          <div className="flex items-center gap-4 sm:gap-6">
            <Link to="/about" className="text-sm hidden sm:inline" style={{ color: 'var(--text-secondary)' }}>About</Link>
            <Link to="/faq" className="text-sm hidden sm:inline" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
            <Link to="/pricing" className="text-sm hidden sm:inline" style={{ color: 'var(--text-secondary)' }}>Pricing</Link>
            <Link to="/docs" className="text-sm font-medium hidden sm:inline" style={{ color: 'var(--accent)' }}>API Docs</Link>
            <Link to="/support" className="text-sm hidden sm:inline" style={{ color: 'var(--text-secondary)' }}>Support</Link>
            {user ? (
              <Link to="/dashboard" className="text-sm" style={{ color: 'var(--text-secondary)' }}>Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm" style={{ color: 'var(--text-secondary)' }}>Login</Link>
                <Link to="/register" className="btn-primary text-sm">Get Started</Link>
              </>
            )}
            <button onClick={toggleTheme} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="apidocs-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-28 sm:pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>

          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
              <Code weight="fill" className="w-4 h-4" style={{ color: 'var(--accent)' }} />
              <span className="text-xs tracking-wider uppercase font-semibold" style={{ color: 'var(--accent)' }}>API Documentation</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
              Developer <span style={{ color: 'var(--accent)' }}>API</span>
            </h1>
            <p className="text-base max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
              Integrate index24hrs.com into your workflow. Submit URLs, manage credits, and track indexing status programmatically.
            </p>
          </div>

          {/* Base URL */}
          <div className="border rounded-xl p-5 mb-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
            <h3 className="font-bold text-sm mb-2">Base URL</h3>
            <CodeBlock code={baseUrl} />
          </div>

          {/* Quick Start */}
          <div className="border rounded-xl p-5 mb-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
            <h3 className="font-bold text-sm mb-3">Quick Start</h3>
            <CodeBlock code={`# 1. Login to get auth cookie
curl -X POST ${baseUrl}/auth/login \\
  -H "Content-Type: application/json" \\
  -c cookies.txt \\
  -d '{"email":"your@email.com","password":"yourpassword"}'

# 2. Submit URLs for indexing
curl -X POST ${baseUrl}/urls/submit \\
  -H "Content-Type: application/json" \\
  -b cookies.txt \\
  -d '{"urls":["https://example.com/page1","https://example.com/page2"]}'

# 3. Check your submissions
curl -X GET ${baseUrl}/urls/submissions \\
  -b cookies.txt`} />
          </div>

          <div className="space-y-6">
            <Section icon={Lock} title="Authentication" description="JWT cookie-based authentication. Login to get an access_token cookie.">
              <EndpointCard method="POST" path="/api/auth/register" description="Create a new account. Purchase credits to start indexing." body={{email: "user@example.com", password: "securePass123", name: "John Doe"}} response={{_id: "abc123", email: "user@example.com", name: "John Doe", credits: 0}} />
              <EndpointCard method="POST" path="/api/auth/login" description="Login with email and password. Returns user data and sets access_token cookie." body={{email: "user@example.com", password: "securePass123"}} response={{_id: "abc123", email: "user@example.com", role: "user", credits: 100}} />
              <EndpointCard method="POST" path="/api/auth/logout" description="Clear authentication cookie." authRequired />
              <EndpointCard method="GET" path="/api/auth/me" description="Get current authenticated user info." authRequired />
              <EndpointCard method="POST" path="/api/auth/forgot-password" description="Send a password reset email with a link to reset your password." body={{email: "user@example.com"}} />
              <EndpointCard method="POST" path="/api/auth/reset-password" description="Reset your password using the token from the reset email." body={{token: "reset_token_from_email", new_password: "newSecurePass123"}} />
            </Section>

            <Section icon={Globe} title="URL Indexing" description="Submit URLs for instant Google indexing. 1 credit per URL, max 150 per batch.">
              <EndpointCard method="POST" path="/api/urls/submit" description="Submit a batch of URLs for indexing. Costs 1 credit per URL. Maximum 150 URLs per batch." authRequired body={{urls: ["https://example.com/page1", "https://example.com/page2"]}} response={{submission_id: "sub_abc123", urls_submitted: 2, credits_used: 2}} />
              <EndpointCard method="GET" path="/api/urls/submissions" description="Get all your URL submissions with indexing status." authRequired />
              <EndpointCard method="GET" path="/api/urls/refresh-status/{submission_id}" description="Refresh the indexing status for a specific submission." authRequired />
            </Section>

            <Section icon={CreditCard} title="Payments" description="Purchase credits with cryptocurrency. 1% service fee on all transactions.">
              <EndpointCard method="GET" path="/api/pricing/plans" description="Get available pricing plans. No authentication required." response={{plans: [{id: "starter", name: "Starter", price: 10, credits: 100}, {id: "growth", name: "Growth", price: 50, credits: 750}, {id: "pro", name: "Pro", price: 150, credits: 5000, limited: true}]}} />
              <EndpointCard method="POST" path="/api/payments/create" description="Create a crypto payment. Supported currencies: btc, eth, bnbbsc, sol, usdtbsc, usdttrc20, ltc, doge, trx, xrp, matic. TRC20 has $5 extra gas fee." authRequired body={{plan_id: "growth", pay_currency: "btc"}} response={{payment_id: "123456", pay_address: "3ABC...", pay_amount: 0.00065, pay_currency: "BTC"}} />
              <EndpointCard method="GET" path="/api/payments/status/{payment_id}" description="Check payment status. Statuses: waiting, confirming, confirmed, finished, failed, expired." authRequired />
              <EndpointCard method="GET" path="/api/payments/history" description="Get your payment/purchase history." authRequired />
            </Section>

            <Section icon={User} title="Profile" description="Manage your account settings.">
              <EndpointCard method="PUT" path="/api/user/profile" description="Update your display name and/or email address." authRequired body={{name: "New Name", email: "new@email.com"}} />
              <EndpointCard method="PUT" path="/api/user/password" description="Change your password. Requires current password." authRequired body={{current_password: "oldPass", new_password: "newPass123"}} />
              <EndpointCard method="GET" path="/api/user/credit-history" description="Get your credit transaction history." authRequired />
            </Section>

            <Section icon={Question} title="Support" description="FAQ and support tickets.">
              <EndpointCard method="GET" path="/api/support/faq" description="Get FAQ categories and items. No authentication required." />
              <EndpointCard method="POST" path="/api/support/tickets" description="Create a support ticket." authRequired body={{subject: "Issue with indexing", message: "My URLs are not being indexed...", category: "technical"}} />
              <EndpointCard method="GET" path="/api/support/tickets" description="Get your support tickets." authRequired />
            </Section>

            {/* Custom Domain */}
            <div className="border rounded-xl p-5" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
              <h3 className="font-bold flex items-center gap-2 mb-3">
                <Lightning weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                Custom Domain Setup
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                index24hrs.com supports custom domain configuration. When using your custom domain, all API endpoints and links (including password reset emails) will use your domain automatically.
              </p>
              <div className="space-y-3">
                <div className="border rounded-lg p-4" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>API Base URL with custom domain</p>
                  <code className="font-mono text-sm" style={{ color: 'var(--text-primary)' }}>https://index24hrs.com/api</code>
                </div>
                <div className="border rounded-lg p-4" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Password reset links will use</p>
                  <code className="font-mono text-sm" style={{ color: 'var(--text-primary)' }}>https://index24hrs.com/reset-password?token=...</code>
                </div>
                <div className="border rounded-lg p-4" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Emails sent from</p>
                  <code className="font-mono text-sm" style={{ color: 'var(--text-primary)' }}>noreply@index24hrs.com</code>
                </div>
              </div>
            </div>

            {/* Rate Limits */}
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-bold text-sm text-amber-400 mb-2">Rate Limits & Notes</h3>
              <ul className="text-xs space-y-1.5" style={{ color: 'var(--text-secondary)' }}>
                <li>Max 150 URLs per submission batch</li>
                <li>5 login attempts before 15-minute lockout</li>
                <li>Password reset links expire in 1 hour</li>
                <li>Payment sessions expire per NOWPayments configuration</li>
                <li>Credits are non-refundable once used for indexing</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t" style={{ borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="font-bold text-sm">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>© 2026 index24hrs.com. Instant indexing for the modern web.</p>
        </div>
      </footer>
    </div>
  );
}
